import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { OnboardingPages } from '../../../../common/src/pages.ts';
import { useHousehold } from '../../data/use_household.tsx';
import { useMusicCache } from '../../util/use_music_cache.tsx';
import { OrgPage } from '../org_page.tsx';
import { Button } from './button.tsx';
import { getOnboardingAppStoreUrl, goToNextOnboardingStep } from './common.tsx';
import { Frame } from './frame.tsx';

export const Demo = () => {
  // "Get Started" navigates to app store in mobile browsers
  // PROBLEM: Does not properly detect in-app for some Android devices
  // SOLUTION: Temporarily eliminate app store link for all Android devices
  // TODO(hewitt): When new Android Studio-based app releases, bring this back (assuming a reliable in-app indicator)
  // const appStoreUrl = isInsideMobileBrowser() && !isDebugOnboardingEnvironment() ? getAppStoreUrl() : undefined;
  const appStoreUrl = getOnboardingAppStoreUrl();
  const { household } = useHousehold();
  const navigate = useNavigate();
  useMusicCache();

  useEffect(() => {
    if (household) {
      void goToNextOnboardingStep(navigate, { from: OnboardingPages.Demo, action: 'householdExists' });
    }
  }, [household, navigate]);

  const beginOnboarding = useCallback(
    async (action: 'getStarted' | 'alreadyHaveHousehold') => {
      if (appStoreUrl) {
        window.location.href = appStoreUrl;
        return;
      }
      await goToNextOnboardingStep(navigate, { from: OnboardingPages.Demo, action });
    },
    [appStoreUrl, navigate],
  );

  const onGetStarted = useCallback(async () => {
    await beginOnboarding('getStarted');
  }, [beginOnboarding]);

  const onHaveHousehold = useCallback(async () => {
    await beginOnboarding('alreadyHaveHousehold');
  }, [beginOnboarding]);

  return (
    <DemoWrapper>
      <Frame
        onboardingPage={OnboardingPages.Demo}
        mainContent={
          <OrgPage
            isDemoPage={true}
            customHeader={
              <OrientationHeader>
                <Headline>Bring your church&apos;s music home</Headline>
                <Subtitle>Set up in under a minute. Your family can join too.</Subtitle>
              </OrientationHeader>
            }
          />
        }
        footerContent={
          <>
            <Button key="getStarted" onClick={onGetStarted}>
              GET STARTED
            </Button>
            {!appStoreUrl && (
              <Button key="haveHousehold" onClick={onHaveHousehold}>
                I ALREADY HAVE A HOUSEHOLD
              </Button>
            )}
          </>
        }
        suppressBackButton={true}
        onEnterKeypress={onGetStarted}
        alignTop={true}
      />
    </DemoWrapper>
  );
};

const DemoWrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const OrientationHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 30px 25px 20px;
  margin: 0 15px;
  text-align: center;
  border-bottom: 1px solid var(--color-button-border);
`;

const Headline = styled.div`
  font-family: Jost-SemiBold, sans-serif;
  font-size: 1.4em;
`;

const Subtitle = styled.div`
  font-size: 0.95em;
  color: var(--color-text-light);
`;
